import type {
    ContestContentField,
    ContestPolicyKind,
    ContestPublicationRule,
} from '../../../stores/contest';
import { MIN_RETENTION_DAYS } from '../general-tab/form';

/** The tab of the contest page that fixes a rule, as named in its address. */
export type RuleTab = 'general' | 'content' | 'pages' | 'policies';

const LANGUAGE_NAMES = { en: 'English', fr: 'French' };

/** Named after the label the Content tab shows them under. */
const CONTENT_FIELD_NAMES: Record<ContestContentField, string> = {
    'game.image': 'tile image',
    'game.subTitle': 'subtitle',
    'game.detailImage': 'screen image',
    'game.description': 'description',
    'game.buttonTitle': 'button label',
    detailImageUrlQc: 'banner',
    confirmationTitle: 'title after an entry',
    confirmationBody: 'text after an entry',
    endedTitle: 'title',
    endedBody: 'text',
};

const SCOPE_NAMES = { contest: 'Contest', page: 'Page', policy: 'Policy' };

type PolicyKindCovering = Exclude<ContestPolicyKind, 'other'>;

/** Named as the Policies tab names them. */
const KIND_NAMES: Record<PolicyKindCovering, string> = {
    contest_rules: 'Contest rules',
    privacy_policy: 'Privacy policy',
    terms_and_conditions: 'Terms and conditions',
};

/** One line per language: `French: banner, button label`. */
function byLanguage(items: { lang: 'en' | 'fr'; name: string }[]): string[] {
    return (['en', 'fr'] as const).flatMap((lang) => {
        const names = items
            .filter((item) => item.lang === lang)
            .map((item) => item.name);
        return names.length
            ? [`${LANGUAGE_NAMES[lang]}: ${names.join(', ')}`]
            : [];
    });
}

/** One line per thing named: `Page home (English, French)`. */
function groupLanguages(
    items: { lang: 'en' | 'fr'; name: string }[]
): string[] {
    const names = [...new Set(items.map((item) => item.name))];
    return names.map((name) => {
        const langs = items
            .filter((item) => item.name === name)
            .map((item) => LANGUAGE_NAMES[item.lang]);
        return `${name} (${langs.join(', ')})`;
    });
}

/**
 * What a rule asks for, worded to read as kept or failed; what it lacks when
 * it fails, one line each; and the tab that fixes it.
 */
export function describeRule(
    rule: ContestPublicationRule,
    policyName: (policyId: number) => string
): { title: string; details: string[]; tab: RuleTab } {
    switch (rule.rule) {
        case 'provinces':
            return {
                title: 'At least one eligible province',
                details: [],
                tab: 'general',
            };
        case 'dates':
            return {
                title: 'The participation period starts before it ends',
                details: [],
                tab: 'general',
            };
        case 'retention':
            return {
                title: `Personal information kept at least ${MIN_RETENTION_DAYS} days`,
                details: [],
                tab: 'general',
            };
        case 'no_purchase_route':
            return {
                title: 'A way to enter without purchase',
                details: rule.ok
                    ? []
                    : [
                          'In both languages: a no-purchase block, or a no-purchase code with a code block to type it in.',
                      ],
                tab: 'general',
            };
        case 'route_capped':
            return {
                title: 'Entries capped per participant',
                details: rule.ok
                    ? []
                    : [
                          'Per day or over the whole contest, so entries without purchase have a limit.',
                      ],
                tab: 'general',
            };
        case 'no_purchase_code_fits':
            return {
                title: 'The no-purchase code fits every code block',
                details: rule.issues.length
                    ? [
                          `Refused by ${rule.issues
                              .map(({ blockKey }) => blockKey)
                              .join(', ')}`,
                      ]
                    : [],
                tab: 'general',
            };
        case 'content':
        case 'ended_message':
            return {
                title:
                    rule.rule === 'content'
                        ? 'Content complete in both languages'
                        : 'Ended message in both languages',
                details: byLanguage(
                    rule.issues.map(({ lang, field }) => ({
                        lang,
                        name: CONTENT_FIELD_NAMES[field],
                    }))
                ),
                tab: 'content',
            };
        case 'home_page':
            return { title: 'A home page', details: [], tab: 'pages' };
        case 'blocks':
            return {
                title: 'Every block configured',
                details: rule.issues.length
                    ? [
                          `To fix: ${rule.issues
                              .map(({ key }) => key)
                              .join(', ')}`,
                      ]
                    : [],
                tab: 'pages',
            };
        case 'addresses': {
            const first = rule.issues[0];
            return {
                title: 'An address in each language',
                details: groupLanguages(
                    rule.issues.map(({ scope, key, policyId, lang }) => ({
                        name:
                            policyId !== null
                                ? `${SCOPE_NAMES[scope]} ${policyName(
                                      policyId
                                  )}`
                                : key
                                ? `${SCOPE_NAMES[scope]} ${key}`
                                : SCOPE_NAMES[scope],
                        lang,
                    }))
                ),
                tab:
                    first?.scope === 'page'
                        ? 'pages'
                        : first?.scope === 'policy'
                        ? 'policies'
                        : 'general',
            };
        }
        case 'rules_policy':
            return { title: 'Official rules', details: [], tab: 'policies' };
        case 'privacy_policy':
            return { title: 'A privacy policy', details: [], tab: 'policies' };
        case 'policies_cover_provinces':
            return {
                title: 'Each kind of policy covers every province',
                details: (Object.keys(KIND_NAMES) as PolicyKindCovering[])
                    .map((kind) => ({
                        kind,
                        provinces: rule.issues
                            .filter((issue) => issue.kind === kind)
                            .map(({ province }) => province),
                    }))
                    .filter(({ provinces }) => provinces.length > 0)
                    .map(
                        ({ kind, provinces }) =>
                            `${KIND_NAMES[kind]}: ${provinces.join(', ')}`
                    ),
                tab: 'policies',
            };
        case 'policies_written':
            return {
                title: 'Every policy written in both languages',
                details: groupLanguages(
                    rule.issues.map(({ policyId, lang }) => ({
                        name: policyName(policyId),
                        lang,
                    }))
                ),
                tab: 'policies',
            };
    }
}
